import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import {
  IGroupAccLog,
  IGroupAccLogsParam,
  IGroupAduit,
  IGroupAduitParam,
  IGroupCenterLogs,
  IGroupCenterLogsParam,
  IGroupCompany,
  IGroupConfirm,
  IGroupConfirmParam,
  IGroupManage,
  IGroupManageParam,
  IGroupOtherConfirm,
  IGroupOtherConfirmParam,
  IGroupSubCompany,
  IGroupSubCompanyParam,
  IToggleGroupAccAduit,
} from '../../types/app-account/group';
import { IFromGroupInvite, IToggleGroupAduit } from '../../types/app-account/group';
import { IEnterpriseItem } from '../../types/common';

const hostUrl = getHostbaseUrl();

/**
 * 账户中心 - 集团管理 - 查询可绑定企业
 */
export async function queryCompanyByCreditCode(creditCode: string) {
  let result = {} as IGroupCompany;

  try {
    const { code, context, message } = await Http.post<IGroupCompany>(
      `${hostUrl}/side/company/searchNotInviteCompany`,
      { creditCode },
    );

    if (code === EAxiosResponseCode.Succeed) {
      result = context || {} as IGroupCompany;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return result;
}

/**
 * 账户中心 - 账户信息 - 查询可加入的集团企业
 */
export async function queryGroupsByCreditCode(creditCode: string) {
  let result = {} as IGroupCompany;

  try {
    const { code, context, message } = await Http.post<IGroupCompany>(
      `${hostUrl}/side/company/searchGroupCompany`,
      { creditCode },
    );

    if (code === EAxiosResponseCode.Succeed) {
      result = context || {} as IGroupCompany;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return result;
}

/**
 * 账户中心 - 账户信息 - 查询受邀集团列表
 */
export async function queryInviteListFromGroups(id: number) {
  let result = [] as IFromGroupInvite[];

  try {
    const { code, context, message } = await Http.post<IFromGroupInvite[]>(
      `${hostUrl}/side/companyGroupApply/companyInvite/list`,
      { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      result = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return result;
}

/**
 * 账户中心 - 账户信息 - 子企业主动解绑集团
 */
export async function unBindFromGroup(id: number, msgCode: string) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/companyUnbind`,
      {
        id,
        code: msgCode,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 账户信息 - 集团主动降级为企业
 */
export async function groupToCompany(param: {
  ids: number[];
  code: string;
  remark?: string;
}) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/groupDemote`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 账户信息 - 受邀集团信息的同意和拒绝
 */
export async function toggleInviteFromGroup(id: number, auditStatus: 1 | 2) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/companyInvite/audit`,
      {
        id,
        auditStatus,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 账户信息 - 申请加入集团
 */
export async function toBeGroupSubCompany(param: {
  /** 企业I */
  companyId?: number;
  /** 集团企业ID */
  companyGroupId?: number;
  /** 申请说明 */
  remark?: string;
}) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/companyApply`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 集团中心(集团审核) - 查询集团审核列表
 */
export async function queryGroupAduitList(param: IGroupAduitParam) {
  let data = [] as IGroupAduit[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IGroupAduitParam;

  try {
    const { code, context, message } = await Http.post<{ data: IGroupAduit[]; total: number }>(
      `${hostUrl}/side/companyGroupApply/groupApplyPage`,
      {
        ...param,
        companyId: param.companyId ? Number(param.companyId) : undefined,
        agentId: param.agentId ? Number(param.agentId) : undefined,
        size: param.size || 12,
        page: param.page || 1,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IGroupAduit[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 集团中心(集团审核) - 审批/驳回企业申请
 */
export async function toggleAduitApply(param: IToggleGroupAduit) {
  param = JSON.parse(JSON.stringify(param)) as IToggleGroupAduit;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/platformAudit`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 集团中心(集团审核) - 导出
 */
export async function exportAuditData(params: IGroupAduitParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/side/companyGroupApply/groupApplyPage/export`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 集团中心(集团管理) - 查询集团管理列表
 */
export async function queryGroupManageList(param: IGroupManageParam) {
  let data = [] as IGroupManage[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IGroupManageParam;

  try {
    const { code, context, message } = await Http.post<{ data: IGroupManage[]; total: number }>(
      `${hostUrl}/side/company/groupPage`,
      {
        ...param,
        agentId: param.agentId ? Number(param.agentId) : undefined,
        id: param.id ? Number(param.id) : undefined,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IGroupManage[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 集团中心(集团管理) - 降级为企业
 */
export async function downgradgeToCompany(param: { ids: number[]; remark: string; code: string }) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/company/demote`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 集团中心(操作记录) - 查询操作记录列表
 */
export async function queryGroupCenterLogs(param: IGroupCenterLogsParam) {
  let data = [] as IGroupCenterLogs[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IGroupCenterLogsParam;

  try {
    const { code, context, message } = await Http.post<{ data: IGroupCenterLogs[]; total: number }>(
      `${hostUrl}/side/companyApplyRecord/page`,
      {
        ...param,
        companyId: param.companyId ? Number(param.companyId) : undefined,
        agentId: param.agentId ? Number(param.agentId) : undefined,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IGroupCenterLogs[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 集团中心(集团管理) - 导出
 */

export async function exportGroupManage(params: IGroupAduitParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/side/company/groupPageExport`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 集团中心(操作记录) - 导出
 */

export async function exportGroupCenterLogs(params: IGroupAduitParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/side/companyApplyRecord/export`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 集团管理 - 子企业列表查询
 */
export async function queryGroupSubCompanyList(param: IGroupSubCompanyParam) {
  let data = [] as IGroupSubCompany[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IGroupSubCompanyParam;

  try {
    const { code, context, message } = await Http.post<{ data: IGroupSubCompany[]; total: number }>(
      `${hostUrl}/side/company/groupMembersPage`,
      {
        ...param,
        id: param.id ? Number(param.id) : undefined,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IGroupSubCompany[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 集团管理 - 子企业列表导出
 */
export async function exportSubComData(params: IGroupSubCompanyParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/side/company/groupMembersPageExport`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 集团管理 - 待我方确认列表查询
 */
export async function queryGroupConfirmList(param: IGroupConfirmParam) {
  let data = [] as IGroupConfirm[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IGroupConfirmParam;

  try {
    const { code, context, message } = await Http.post<{ data: IGroupConfirm[]; total: number }>(
      `${hostUrl}/side/companyGroupApply/groupMembersPage`,
      {
        ...param,
        id: param.id ? Number(param.id) : undefined,
        size: param.size || 12,
        page: param.page || 1,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IGroupConfirm[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 集团管理 - 待对方确认列表查询
 */
export async function queryGroupOtherConfirmList(param: IGroupOtherConfirmParam) {
  let data = [] as IGroupOtherConfirm[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IGroupOtherConfirmParam;

  try {
    const { code, context, message } = await Http.post<{ data: IGroupOtherConfirm[]; total: number }>(
      `${hostUrl}/side/companyGroupApply/groupInvitePage`,
      {
        ...param,
        id: param.id ? Number(param.id) : undefined,
        size: param.size || 12,
        page: param.page || 1,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IGroupOtherConfirm[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 集团管理 - 待对方确认列表导出
 */
export async function exportGroupOtherConfirmData(params: IGroupOtherConfirmParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/side/companyGroupApply/groupInvitePageExport`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 集团管理 - 操作记录列表查询
 */
export async function queryGroupAccLogs(param: IGroupAccLogsParam) {
  let data = [] as IGroupAccLog[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IGroupAccLogsParam;

  try {
    const { code, context, message } = await Http.post<{ data: IGroupAccLog[]; total: number }>(
      `${hostUrl}/side/companyApplyRecord/groupPage`,
      param,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IGroupAccLog[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 集团管理 - 操作记录列表导出
 */
export async function exportGroupAccLogs(params: IGroupAccLogsParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/side/companyApplyRecord/groupPageExport`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 集团管理 - 待我方确认 - 审批/驳回企业申请
 */
export async function toggleGroupAccAduitApply(param: IToggleGroupAccAduit) {
  param = JSON.parse(JSON.stringify(param)) as IToggleGroupAccAduit;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/groupAuditCompany`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 集团管理 - 子企业管理 - 解除绑定
 */
export async function unBindCompany(param: { ids: number[]; code: string }) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/company/groupUnbind`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 集团管理 - 待对方确认 - 撤销邀请企业
 */
export async function cancelInviteCompany(id: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/cancelInvite`,
      { id },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 集团管理 - 待对方确认 - 邀请企业加入集团
 */
export async function inviteCompanyBind(param: {
  /** 企业I */
  id?: number;
  /** 申请说明 */
  remark?: string;
}) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/invite`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 账户信息 - 撤销申请
 */
export async function cancelGroupApply(companyId: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/cancel`,
      { companyId },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 账户信息 - 申请为集团
 */
export async function companyToGroup(id: number, remark: string) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/companyGroupApply/groupApply`,
      {
        id,
        remark,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 集团中心、集团管理 - 操作记录 - 企业列表
 * @param isGroupManage 是否集团管理 - true:集团管理 false:集团中心
 */
export async function queryRecordCompanyList(isGroupManage: boolean) {
  let data = [] as IEnterpriseItem[];

  try {
    const { code, context, message } = await Http.post<Array<IEnterpriseItem>>(`${hostUrl}/side/companyApplyRecord/${isGroupManage ? 'companyGroupList' : 'companyList'}`);

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || [];

      data = res || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return { data };
}

/**
 * 账户中心 -集团中心、集团管理 - 申请操作授权子企业
 */
export async function applyAuthOperateCompany(ids: number[]) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/company/group-operate-auth-apply`,
      { ids },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 -集团中心、集团管理 - 取消操作授权子企业
 */
export async function cancelAuthOperateCompany(ids: number[]) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/company/group-operate-auth-cancel`,
      { ids },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 -集团中心、集团管理 - 取消操作授权子企业
 */
export async function voidAuthOperateCompany(ids: number[]) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/company/group-operate-auth-void`,
      { ids },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 -集团中心、集团管理 - 审核操作授权子企业
 */
export async function confirmAuthOperateCompany(verifyCode: string) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/company/group-operate-auth-confirm`,
      { verifyCode },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 -集团中心、集团管理 - 子企业授权解除
 */
export async function revokeAuthOperateCompany(verifyCode: string) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/side/company/group-operate-auth-revoke`,
      { verifyCode },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

